// src/app/pages/auth/ResetPasswordPage.jsx
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword, verifyResetToken } from "../../features/auth/authApi.js";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Reset token is missing from the URL.");
        setVerifying(false);
        return;
      }
      
      try {
        await verifyResetToken(token);
        setTokenValid(true);
      } catch (err) {
        const errorData = err?.response?.data;
        setError(errorData?.detail || "Invalid or expired reset token.");
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    
    // Validate password strength
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    
    setLoading(true);
    
    try {
      await resetPassword(token, password);
      setSuccess(true);
      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      const errorData = err?.response?.data;
      setError(errorData?.detail || err?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
        <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
        <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-8">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-white">Invalid Reset Link</h1>
          </div>
          
          <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">
            <div className="flex items-start gap-2">
              <div className="mt-0.5">⚠️</div>
              <div>{error || "This password reset link is invalid or has expired."}</div>
            </div>
          </div>
          
          <div className="text-center">
            <Link
              to="/auth/forgot-password"
              className="inline-block text-indigo-400 hover:text-indigo-300 font-medium"
            >
              Request a new reset link
            </Link>
            <span className="mx-2 text-slate-500">•</span>
            <Link
              to="/login"
              className="inline-block text-indigo-400 hover:text-indigo-300 font-medium"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-8">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-white">Set New Password</h1>
          <p className="text-sm text-slate-400 mt-1">
            Choose a strong password for your account
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="text-sm text-green-300 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
              <div className="flex items-start gap-2">
                <div className="mt-0.5">✅</div>
                <div>
                  <p className="font-medium">Password Reset Successful!</p>
                  <p className="mt-1">
                    Your password has been updated. You can now login with your new password.
                  </p>
                  <p className="mt-2 text-xs">
                    Redirecting to login in 3 seconds...
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <Link
                to="/login"
                className="inline-block text-indigo-400 hover:text-indigo-300 font-medium"
              >
                Go to Login
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
                minLength={8}
                disabled={loading}
              />
              <p className="text-xs text-slate-500 mt-1">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
                minLength={8}
                disabled={loading}
              />
            </div>

            {error && (
              <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5">⚠️</div>
                  <div>{error}</div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-500 transition font-semibold py-2 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            <div className="text-center text-sm text-slate-400">
              <Link
                to="/login"
                className="text-indigo-400 hover:text-indigo-300 font-medium"
              >
                Back to Login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}